import { Injectable } from '@angular/core';
import { FeatureFlag, NewPermission, Permission } from '@core/enums';
import { Store } from '@ngxs/store';
import { CustomObjectsService } from '@services/custom-objects.service';
import { AccountState } from '@state/account/state';
import { UserState } from '@state/user/state';
import { CompaniesService } from 'app/modules/companies/companies.service';
import { Company } from 'app/modules/companies/companies.types';
import { ContactsService } from 'app/modules/contacts/contacts.service';
import { Contact } from 'app/modules/contacts/contacts.types';
import { ProjectsService } from 'app/modules/projects/projects.service';
import { PropertiesService } from 'app/modules/properties/properties.service';
import { ObjectsManagerState } from 'app/modules/settings/objects-manager/state/state';
import { RelatedToType } from 'app/shared/enums';
import { Dictionary } from 'app/shared/models';
import { Observable, of, map, switchMap } from 'rxjs';
import { LookupContract, LookupFilter, LookupQuickAdd, LookupResult } from 'tsui';

@Injectable({ providedIn: 'root' })
export class LookupService implements LookupContract {
  private lookupDictionary: Dictionary<(query: string, customObjectName?: string) => Observable<any[]>>;
  private newPermissionsEnabled = this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NEW_PERMISSIONS));

  constructor(
    private readonly store: Store,
    private readonly projectsService: ProjectsService,
    private readonly propertiesService: PropertiesService,
    private readonly companiesService: CompaniesService,
    private readonly contactsService: ContactsService,
    private readonly customObjectsService: CustomObjectsService,
  ) {
    this.lookupDictionary = {
      [RelatedToType.Project]: (query: string) => {
        return this.projectsService
          .getProjectsNoPipeline(
            ['id', 'title'],
            {},
            0,
            20,
            'title',
            'asc',
            query,
            false,
            null,
            false,
            null,
            this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS)),
          )
          .pipe(map((response) => response.projects.map((x) => ({ id: x.id, label: x?.name || x?.title }))));
      },
      [RelatedToType.Property]: (query: string) => {
        return this.propertiesService
          .getProperties(
            ['id', 'name', 'address_string'],
            {},
            0,
            20,
            'name',
            'asc',
            query,
            '',
            '',
            this.store.selectSnapshot(AccountState.isFeatureEnabled(FeatureFlag.NESTED_FILTERS)),
          )
          .pipe(map((response) => response.properties.map((x) => ({ id: x.id, label: x.name }))));
      },
      [RelatedToType.Company]: (query: string) => {
        return this.companiesService
          .search({ fjson: '', q: query, select: ['id', 'name'] }, 0, 20)
          .pipe(map((response) => response.results.map((x) => ({ id: x.id, label: x.name }))));
      },
      [RelatedToType.Contact]: (query: string) => {
        return this.contactsService
          .search({ fjson: '', q: query, select: ['id', 'name', 'company'] }, 0, 20)
          .pipe(map((response) => response.results.map((x) => ({ id: x.id, label: x.name, company: x.company }))));
      },
      [RelatedToType.User]: (query: string) => {
        const users = this.store.selectSnapshot(UserState.getUsers);
        return of(
          users
            .filter((user) => user.name.toLowerCase().includes(query.toLowerCase()))
            .map((x) => ({ id: x.id, label: x.name })),
        );
      },
      [RelatedToType.CustomObject]: (query: string, customObjectName: string) => {
        const customObjects = this.store.selectSnapshot(ObjectsManagerState.get);
        const customField = customObjects?.find(({ name }) => name === customObjectName);

        if (!customField) {
          return of([]);
        }

        const account = this.store.selectSnapshot(AccountState.getAccount);
        const searchField = customField.fields?.find((field) => field.id === customField.search_custom_field_id);

        return this.customObjectsService
          .getValues(+account.id, customField.id, undefined, undefined, 0, 20, 'name', 'asc', query)
          .pipe(map((response) => response.values.map((obj) => ({ id: obj.id, label: obj[searchField.name] }))));
      },
    };
  }

  lookFor(model: LookupFilter): Observable<LookupResult[]> {
    const lookupFn = this.lookupDictionary[model.relatedTo];

    if (!lookupFn) {
      return this.lookupDictionary.CustomObject(model.query, model.relatedTo);
    }

    return this.lookupDictionary[model.relatedTo](model.query);
  }

  createAndLink(model: LookupQuickAdd): Observable<any> {
    let observable = null;

    switch (model.recordType) {
      case RelatedToType.Company:
        observable = this.companiesService.createCompany({ name: model.name } as Company);
        break;
      case RelatedToType.Contact:
        observable = this.contactsService.createContact({ name: model.name } as Contact);
        break;
    }

    return observable.pipe(
      switchMap((record: { id: number }) => {
        switch (model.relatedTo) {
          case RelatedToType.Project:
            return this.projectsService.saveProject(
              {
                id: model.linkedTo.id,
                [model.fieldName]: model.multiSelect
                  ? [...model.linkedTo[model.fieldName], `${record.id}|${model.name}`]
                  : record.id,
              },
              model.linkedTo,
              false,
            );
          default:
            return of(null);
        }
      }),
    );
  }

  doesSupportCreate(relatedTo: RelatedToType, recordType: string): boolean {
    if (!recordType) {
      return false;
    }
    const isStandardField = !recordType.startsWith('co_');
    const relatedToProjectField = RelatedToType.Project === relatedTo && isStandardField;
    const relatedToLoanQuoteField = RelatedToType.LoanQuote === relatedTo && isStandardField;

    const result = relatedToProjectField || relatedToLoanQuoteField;

    if (recordType === RelatedToType.Contact) {
      return result && this.canCreateContact();
    }

    if (recordType === RelatedToType.Company) {
      return result && this.canCreateCompany();
    }

    return result;
  }

  private canCreateContact(): boolean {
    if (this.newPermissionsEnabled) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.CONTACTS_CREATE));
    }

    return this.store.selectSnapshot(UserState.hasPermission(Permission.CONTACTS_ADD));
  }

  private canCreateCompany(): boolean {
    if (this.newPermissionsEnabled) {
      return this.store.selectSnapshot(UserState.hasPermission(NewPermission.COMPANIES_CREATE));
    }

    return this.store.selectSnapshot(UserState.hasPermission(Permission.CONTACTS_ADD));
  }
}
